// =============================================
// System Admin Organizations Page
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Search,
  Landmark,
  Building2,
  Phone,
  Mail,
  X,
  UserPlus,
  ChevronLeft,
  CheckCircle2,
  AlertCircle,
  User
} from 'lucide-react';
import {
  adminApi,
  type OrganizationDto,
  type CreateOrganizationRequest,
  type CreateOrganizationManagerRequest
} from '../../services/api';
import { sysAdminColors as colors } from '../../styles/colors';

const orgTypes = [
  { value: 1, label: 'עירייה', icon: Landmark },
  { value: 2, label: 'רשת גנים', icon: Building2 },
  { value: 3, label: 'אחר', icon: Building2 },
];

export default function SysAdminOrganizations() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<OrganizationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showManagerModal, setShowManagerModal] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const result = await adminApi.getOrganizations();
        if (result.success && result.data) {
          setOrganizations(result.data);
        }
      } catch (err) {
        console.error('Failed to load organizations:', err);
      } finally {
        setLoading(false);
      }
    };
    loadOrganizations();
  }, [refreshKey]);

  const filteredOrgs = organizations.filter(org =>
    org.name.includes(searchQuery) ||
    org.contactName?.includes(searchQuery) ||
    org.contactPhone?.includes(searchQuery)
  );

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text, marginBottom: '4px' }}>
            ארגונים
          </h1>
          <p style={{ fontSize: '14px', color: colors.textLight }}>
            ניהול ארגונים - עיריות, רשתות גנים וגופים נוספים
          </p>
        </div>
        <motion.button
          onClick={() => setShowCreateModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 20px',
            borderRadius: '12px',
            border: 'none',
            background: `linear-gradient(135deg, ${colors.primary} 0%, #6d28d9 100%)`,
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={18} />
          ארגון חדש
        </motion.button>
      </div>

      {/* Success Message */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              backgroundColor: colors.successLight,
              borderRadius: '10px',
            }}
          >
            <CheckCircle2 size={18} color={colors.success} />
            <span style={{ fontSize: '14px', color: colors.success }}>{successMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search */}
      <div style={{ position: 'relative' }}>
        <Search size={18} color={colors.textLight} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="חיפוש ארגון..."
          style={{
            width: '100%',
            padding: '12px 44px 12px 16px',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            fontSize: '14px',
            outline: 'none',
            direction: 'rtl',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Organizations List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
          טוען ארגונים...
        </div>
      ) : filteredOrgs.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <Landmark size={48} color={colors.textLight} style={{ margin: '0 auto 16px' }} />
          <p style={{ fontSize: '16px', color: colors.text, fontWeight: '500' }}>
            {searchQuery ? 'לא נמצאו ארגונים' : 'אין ארגונים עדיין'}
          </p>
          <p style={{ fontSize: '14px', color: colors.textLight, marginTop: '4px' }}>
            {searchQuery ? 'נסו חיפוש אחר' : 'לחצו על "ארגון חדש" להוספת ארגון ראשון'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredOrgs.map((org, index) => (
            <motion.div
              key={org.publicId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: '16px', flex: 1 }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    backgroundColor: colors.primaryLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {org.organizationType === 1 ? (
                      <Landmark size={24} color={colors.primary} />
                    ) : (
                      <Building2 size={24} color={colors.primary} />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>
                        {org.name}
                      </h3>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        backgroundColor: org.isActive ? colors.successLight : colors.warningLight,
                        color: org.isActive ? colors.success : colors.warning,
                      }}>
                        {org.isActive ? 'פעיל' : 'לא פעיל'}
                      </span>
                    </div>
                    <p style={{ fontSize: '13px', color: colors.textLight, marginBottom: '8px' }}>
                      {org.organizationTypeName} | {org.kindergartenCount} גנים
                    </p>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                      {org.contactName && (
                        <span style={{ fontSize: '13px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <User size={14} /> {org.contactName}
                        </span>
                      )}
                      {org.contactPhone && (
                        <span style={{ fontSize: '13px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Phone size={14} /> {org.contactPhone}
                        </span>
                      )}
                      {org.contactEmail && (
                        <span style={{ fontSize: '13px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Mail size={14} /> {org.contactEmail}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <motion.button
                    onClick={() => setShowManagerModal(org.publicId)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.primary}30`,
                      backgroundColor: colors.primaryLight,
                      color: colors.primary,
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <UserPlus size={16} />
                    מנהל
                  </motion.button>
                  <motion.button
                    onClick={() => navigate(`/admin/kindergartens?org=${org.publicId}`)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`,
                      backgroundColor: 'white',
                      color: colors.text,
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <ChevronLeft size={16} />
                    כניסה
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Organization Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateOrganizationModal
            onClose={() => setShowCreateModal(false)}
            onCreated={(org) => {
              setOrganizations(prev => [...prev, org]);
              setShowCreateModal(false);
              showSuccess(`ארגון "${org.name}" נוצר בהצלחה`);
            }}
          />
        )}
      </AnimatePresence>

      {/* Create Manager Modal */}
      <AnimatePresence>
        {showManagerModal && (
          <CreateManagerModal
            orgPublicId={showManagerModal}
            orgName={organizations.find(o => o.publicId === showManagerModal)?.name || ''}
            onClose={() => setShowManagerModal(null)}
            onCreated={() => {
              setShowManagerModal(null);
              showSuccess('מנהל הארגון נוצר בהצלחה');
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ==========================================
// Create Organization Modal
// ==========================================

function CreateOrganizationModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (org: OrganizationDto) => void;
}) {
  const [name, setName] = useState('');
  const [orgType, setOrgType] = useState(1);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('יש להזין שם ארגון');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const request: CreateOrganizationRequest = {
        name: name.trim(),
        organizationType: orgType,
        contactName: contactName.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
      };

      const result = await adminApi.createOrganization(request);
      if (result.success && result.data) {
        onCreated(result.data);
      } else {
        setError(result.message || 'שגיאה ביצירת ארגון');
      }
    } catch {
      setError('שגיאה ביצירת ארגון');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '10px',
    border: `1px solid ${colors.border}`,
    fontSize: '14px',
    outline: 'none',
    direction: 'rtl' as const,
    boxSizing: 'border-box' as const,
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '16px',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '28px',
        }}
        dir="rtl"
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: colors.text }}>ארגון חדש</h2>
          <button
            onClick={onClose}
            style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '8px' }}
          >
            <X size={20} color={colors.textLight} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 14px',
            backgroundColor: '#fef2f2',
            borderRadius: '8px',
            marginBottom: '16px',
          }}>
            <AlertCircle size={16} color={colors.error} />
            <span style={{ fontSize: '13px', color: colors.error }}>{error}</span>
          </div>
        )}

        {/* Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px', display: 'block' }}>
              שם הארגון *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="לדוגמה: עיריית תל אביב"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px', display: 'block' }}>
              סוג ארגון
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {orgTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setOrgType(type.value)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '10px',
                    border: `2px solid ${orgType === type.value ? colors.primary : colors.border}`,
                    backgroundColor: orgType === type.value ? colors.primaryLight : 'white',
                    color: orgType === type.value ? colors.primary : colors.text,
                    fontSize: '13px',
                    fontWeight: orgType === type.value ? '600' : '400',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <type.icon size={18} />
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px', display: 'block' }}>
              איש קשר
            </label>
            <input
              type="text"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="שם איש קשר"
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px', display: 'block' }}>
                טלפון
              </label>
              <input
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="050-000-0000"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px', display: 'block' }}>
                אימייל
              </label>
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="email@example.com"
                style={{ ...inputStyle, direction: 'ltr' as const }}
              />
            </div>
          </div>

          {/* Submit Button */}
          <motion.button
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '14px',
              marginTop: '8px',
              borderRadius: '12px',
              border: 'none',
              background: `linear-gradient(135deg, ${colors.primary} 0%, #6d28d9 100%)`,
              color: 'white',
              fontSize: '15px',
              fontWeight: 'bold',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.7 : 1,
            }}
            whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
            whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
          >
            {isSubmitting ? 'יוצר...' : 'צור ארגון'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ==========================================
// Create Manager Modal
// ==========================================

function CreateManagerModal({
  orgPublicId,
  orgName,
  onClose,
  onCreated,
}: {
  orgPublicId: string;
  orgName: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!firstName.trim() || !lastName.trim() || !phone.trim() || !username.trim() || !password.trim()) {
      setError('יש למלא את כל השדות החובה');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const request: CreateOrganizationManagerRequest = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        username: username.trim(),
        password: password.trim(),
      };

      const result = await adminApi.createOrganizationManager(orgPublicId, request);
      if (result.success) {
        onCreated();
      } else {
        setError(result.message || 'שגיאה ביצירת מנהל');
      }
    } catch {
      setError('שגיאה ביצירת מנהל');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '10px',
    border: `1px solid ${colors.border}`,
    fontSize: '14px',
    outline: 'none',
    direction: 'rtl' as const,
    boxSizing: 'border-box' as const,
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '16px',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '28px',
        }}
        dir="rtl"
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: colors.text }}>הקמת מנהל ארגון</h2>
            <p style={{ fontSize: '13px', color: colors.textLight }}>{orgName}</p>
          </div>
          <button
            onClick={onClose}
            style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '8px' }}
          >
            <X size={20} color={colors.textLight} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 14px',
            backgroundColor: '#fef2f2',
            borderRadius: '8px',
            marginBottom: '16px',
          }}>
            <AlertCircle size={16} color={colors.error} />
            <span style={{ fontSize: '13px', color: colors.error }}>{error}</span>
          </div>
        )}

        {/* Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px', display: 'block' }}>
                שם פרטי *
              </label>
              <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px', display: 'block' }}>
                שם משפחה *
              </label>
              <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div>
            <label style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px', display: 'block' }}>
              טלפון *
            </label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="050-000-0000" style={inputStyle} />
          </div>

          <div>
            <label style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px', display: 'block' }}>
              אימייל
            </label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ ...inputStyle, direction: 'ltr' as const }} />
          </div>

          <div>
            <label style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px', display: 'block' }}>
              שם משתמש *
            </label>
            <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} style={{ ...inputStyle, direction: 'ltr' as const }} />
          </div>

          <div>
            <label style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '6px', display: 'block' }}>
              סיסמה *
            </label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={{ ...inputStyle, direction: 'ltr' as const }} />
          </div>

          <motion.button
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '14px',
              marginTop: '8px',
              borderRadius: '12px',
              border: 'none',
              background: `linear-gradient(135deg, ${colors.primary} 0%, #6d28d9 100%)`,
              color: 'white',
              fontSize: '15px',
              fontWeight: 'bold',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.7 : 1,
            }}
            whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
            whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
          >
            {isSubmitting ? 'יוצר...' : 'צור מנהל'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
